import {css, StyleSheet} from 'aphrodite';
import {getNamedType} from 'query-api-graphql';
import React from 'react';

import {RelatedResultsFormState} from 'analysis-tool/data-frame-creation/schemas/RegistrySchemaDataFrameCreationTypes';
import {getAndAssertObjectType} from 'analysis-tool/data-frame-creation/schemas/RegistrySchemasDataFrameCreationUtils';
import {assert} from 'Asserts';
import {EMPTY_SELECTION_SET} from 'query-api/graphql-field-selector/CompositeField';
import {GraphQLField} from 'query-api/graphql-field-selector/GraphQLFieldSelectorTypes';
import {QueryApiIntrospection} from 'query-api/useQueryApiIntrospection';
import {ResultSchemaSelectorIdInput} from 'results/ResultSchemaSelector';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY_DARK, GRAY_DARKER} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {RadioButtonSelector} from 'ui-library/inputs/RadioButtonGroup';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import pick from 'util/pick';

import GraphQLFieldSelectorWrapper from './GraphQLFieldSelectorWrapper';
import {RESULT_SCHEMA_FIELD_SELECTOR_LOGICAL_DEPTH_LIMIT} from './SchemaDatasetCreationConstants';

interface RelatedResultsFormProps {
  entityListingField: GraphQLField;
  formState: RelatedResultsFormState;
  onChangeFormState: (newFormState: RelatedResultsFormState) => void;
  onRemove: () => void;
  isBacklinkFieldSelected: (backlinkFieldName: string) => boolean;
  queryApiIntrospection: QueryApiIntrospection;
}

/**
 * Form for the user to specify related results to be joined onto an entity listing query api query.
 *
 * Result joining explanation:
 *
 * Here is what a Query API SDL would roughly look like with a single Result schema that has two link fields
 * to an Entity schema:
 *
 * ```
 * type MyResult {               // corresponding Result Schema API ID: "assaysch_123"
 *   id: ID
 *   count: Int
 *   parentPlasmid1: Plasmid!
 *   parentPlasmid2: Plasmid!
 * }
 *
 * type Plasmid {                // corresponding Tag Schema API ID: "ts_123"
 *   id: ID
 *   name: String!
 *   _backlinkMyResults(         // field marked as a backlink field by Benchling-specific introspection
 *     joinKey: String!
 *   ): [MyResult!]
 * }
 *
 * type Query {
 *   plasmids: [Plasmid!]
 * }
 * ```
 *
 * `_backlinkMyResults` is a "backlink" because there's no explicit "MyResult" link field on the Plasmid type;
 * rather, the relationship happens "backwards", from `MyResult.plasmid1` or `MyResult.plasmid2`.
 *
 * Thus, in order to join together `Plasmids` and `MyResults`, we need to tell GraphQL which `MyResult` field
 * links to `Plasmid`. It's ambiguous in this case; it could be `plasmid1` or `plasmid2`.
 * This is what the `joinKey` argument is for: it could be specified as "plasmid1" or "plasmid2".
 *
 * A potential final query to build is:
 * ```
 * {
 * plasmids {
 *   id
 *   _backlinkMyResults(joinKey: "plasmid1") {
 *     someCount
 *   }
 * }
 * ```
 *
 * The state to use `_backlinkMyResults` is stored in `InitializedRelatedResultsFormState['backlinkFieldName']`
 * A `ResultSchemaSelector` is rendered to allow the user to select this.
 *
 * The state to use `joinKey: "plasmid1"` is stored in `InitializedRelatedResultsFormState['joinKeyFieldName']`
 * A `Dropdown` is rendered to allow the user to select this when there are multiple possible entity link fields
 * on the selected result schema.
 *
 * The state to specify the selections (`{someCount}`) is stored in InitializedRelatedResultsFormState['backlinkSelections']`
 * A `GraphQLFieldSelectorWrapper` is rendered to allow the users to update these selections.
 *
 * @package
 */
export default function RelatedResultsForm({
  entityListingField,
  formState,
  onChangeFormState,
  onRemove,
  queryApiIntrospection,
  isBacklinkFieldSelected,
}: RelatedResultsFormProps): JSX.Element {
  const entityType = getAndAssertObjectType(entityListingField);

  const backlinkField =
    formState.backlinkFieldName != null ? entityType.getFields()[formState.backlinkFieldName] : null;

  const resultType = backlinkField != null ? getAndAssertObjectType(backlinkField) : null;

  const joinKeyField =
    resultType != null && formState.joinKeyFieldName
      ? resultType.getFields()[formState.joinKeyFieldName]
      : null;
  const possibleJoinKeyFields =
    resultType != null ? queryApiIntrospection.getPossibleJoinKeyFields(entityType, resultType) : null;

  return (
    <div
      {...getTestProps({component: 'RelatedResultsForm', element: 'root'})}
      className={css(styles.wrapper)}
    >
      <FieldLabel label="Result schema" />
      <div className={css(styles.resultSchemaRow)}>
        <div className={css(styles.resultSchemaWrapper)}>
          <ResultSchemaSelectorIdInput
            selectedSchemaId={
              formState.backlinkFieldName == null
                ? null
                : queryApiIntrospection.backlinkFieldToTargetSchemaId(
                    entityType.getFields()[formState.backlinkFieldName]
                  )
            }
            onChange={(schema) => {
              if (schema == null) {
                // This should not happen since `shouldShowNoSchemaOption` is false, but handle it just in
                // case it does and for typesafety.
                onChangeFormState({
                  ...formState,
                  backlinkFieldName: null,
                  joinKeyFieldName: null,
                  backlinkSelections: EMPTY_SELECTION_SET,
                });
                return;
              }
              const newBacklinkField = queryApiIntrospection.targetSchemaIdToBacklinkField(
                entityType,
                schema.id
              );
              // This shouldn't happen due to the filter prop below, but add it just in case.
              assert(newBacklinkField != null, 'No backlink field found for schema', {
                entityListingField: entityListingField.name,
                schemaId: schema.id,
              });

              const newPossibleJoinKeyFields = queryApiIntrospection.getPossibleJoinKeyFields(
                entityType,
                getAndAssertObjectType(newBacklinkField)
              );
              assert(newPossibleJoinKeyFields.length > 0, 'No possible join keys found on backlink field', {
                backlinkFieldName: newBacklinkField.name,
                ofType: getNamedType(newBacklinkField.type).name,
              });

              onChangeFormState({
                ...formState,
                backlinkFieldName: newBacklinkField.name,
                joinKeyFieldName:
                  newPossibleJoinKeyFields.length === 1 ? newPossibleJoinKeyFields[0].name : null,
                backlinkSelections: EMPTY_SELECTION_SET,
              });
            }}
            filter={(schema) => {
              const schemaBacklinkField = queryApiIntrospection.targetSchemaIdToBacklinkField(
                entityType,
                schema.id
              );
              return (
                // Do not show schemas that do not have a corresponding backlink field on `entityListingField`
                schemaBacklinkField != null &&
                // Do not show schemas that were selected in a different part of the form
                (!isBacklinkFieldSelected(schemaBacklinkField.name) || backlinkField === schemaBacklinkField)
              );
            }}
            shouldShowNoSchemaOption={false}
          />
        </div>
        <BenchlingIcon
          className={css(styles.deleteIcon)}
          icon="delete"
          onClick={() => {
            onRemove();
          }}
        />
      </div>
      {resultType != null && possibleJoinKeyFields != null && possibleJoinKeyFields.length > 1 && (
        <div className={css(styles.joinKeyDropdownWrapper)}>
          <SelectDropdown
            displayName={
              joinKeyField == null
                ? 'Select field to join on'
                : `Join on: ${queryApiIntrospection.getFieldDisplayName(joinKeyField)}`
            }
            menu={
              <Menu value={joinKeyField}>
                {possibleJoinKeyFields.map((field) => (
                  <Option
                    key={field.name}
                    value={field}
                    onSelect={() => {
                      // No need to clear the selection state since the result schema isn't changing
                      onChangeFormState({...formState, joinKeyFieldName: field.name});
                    }}
                  >
                    {queryApiIntrospection.getFieldDisplayName(field)}
                  </Option>
                ))}
              </Menu>
            }
          />
        </div>
      )}
      {formState.backlinkFieldName != null && (
        <>
          <RadioButtonSelector
            hasBottomSpacing={true}
            selectedValue={formState.onlyIncludeLatestItem}
            options={[
              {label: 'Only include latest results', value: true},
              {label: 'Include all results', value: false},
            ]}
            onChange={(newValue: boolean) => {
              onChangeFormState({...formState, onlyIncludeLatestItem: newValue});
            }}
          />
          {!formState.onlyIncludeLatestItem && (
            <div className={css(styles.resultLimitBanner)}>
              <InlineBanner
                bannerType={InlineBannerType.INFO}
                text={`Only the ${(1000).toLocaleString()} most recent results per entity will be included.`}
              />
            </div>
          )}
        </>
      )}
      {resultType != null && backlinkField != null && formState.joinKeyFieldName != null && (
        <GraphQLFieldSelectorWrapper
          field={backlinkField}
          logicalDepthLimit={RESULT_SCHEMA_FIELD_SELECTOR_LOGICAL_DEPTH_LIMIT}
          selectionSet={formState.backlinkSelections}
          setSelectionSet={(newSelectionSet) => {
            onChangeFormState({...formState, backlinkSelections: newSelectionSet});
          }}
          getFieldDisplayName={(field) =>
            // Special-case the top-level field.
            // It's named _<org><ResultSchemaGQLTypeName> but we just want to show <result_schema_name>.
            field === backlinkField
              ? queryApiIntrospection.getTypeDisplayName(resultType)
              : queryApiIntrospection.getFieldDisplayName(field)
          }
          {...pick(
            queryApiIntrospection,
            'getTypeDisplayName',
            'graphQLSchema',
            'isDeprecatedField',
            'isBacklinkField',
            'isUserDefinedField',
            'isUserComputedField'
          )}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    marginBottom: 28,
  },
  resultSchemaRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 6,
  },
  resultSchemaWrapper: {
    flexGrow: 1,
  },
  deleteIcon: {
    flexShrink: 0,
    marginLeft: 6,
    width: 16,
    height: 16,
    color: GRAY_DARK,
    cursor: 'pointer',
    ':hover': {
      color: GRAY_DARKER,
    },
  },
  joinKeyDropdownWrapper: {
    marginBottom: 6,
  },
  resultLimitBanner: {
    marginBottom: 8,
  },
});
